/** Tab-local original intent; the server owns durable send reservation and proof. */
import { captureFeedbackConversationBoundary, executeFeedbackSend, reconcileFeedbackMessage,
  reserveFeedbackSend, resolveFeedbackSend } from './api';
import { refreshFeedbackConversation } from './queryClient';
import type { AppendFeedbackMessageRequest, FeedbackConversationScope, FeedbackConversationTarget,
  FeedbackMessageReceipt, FeedbackMessageResult, FeedbackPendingAttemptRequest, FeedbackPendingResult,
  FeedbackSendReservationRequest } from './types';

export type FeedbackSendState =
  | { status: 'ready' | 'sending' | 'unknown' | 'unavailable' }
  | { status: 'reserved' | 'accepted' | 'rejected' | 'accepted_resolved' | 'rejected_resolved'; result: FeedbackPendingResult }
  | { status: 'reconciled'; result: FeedbackMessageResult };
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const uuid = (value: unknown): value is string => typeof value === 'string'
  && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
// Match Python str.strip exactly, including C0 separators/NEL and excluding BOM.
const pythonTrim = (value: string) => value.replace(/^[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\u0009-\u000d\u001c-\u0020\u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g, '');
const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
function keys(value: object, expected: string): boolean {
  return Object.keys(value).sort().join(',') === expected.split(',').sort().join(',');
}
function receipt(value: FeedbackMessageReceipt, original?: AppendFeedbackMessageRequest): FeedbackMessageReceipt {
  if (!value || !keys(value, 'message_id,conversation_id,feedback_revision_id,sequence,writing_id,writing_version,writing_disposition')
    || !uuid(value.message_id) || !uuid(value.conversation_id) || !uuid(value.feedback_revision_id)
    || typeof value.sequence !== 'string' || !/^[1-9][0-9]{0,18}$/.test(value.sequence)
    || BigInt(value.sequence) > 9223372036854775807n
    || !['none', 'consumed', 'preserved'].includes(value.writing_disposition)
    || (value.writing_id === null ? value.writing_version !== null || value.writing_disposition !== 'none'
      : !uuid(value.writing_id) || !Number.isInteger(value.writing_version) || (value.writing_version ?? 0) < 1 || (value.writing_version ?? 0) > 2147483647)
    || (original && (!same(value.feedback_revision_id, original.expected_feedback_revision_id)
      || (original.writing_id === null ? value.writing_id !== null
        : value.writing_id === null || !same(value.writing_id, original.writing_id))
      || (original.writing_id !== null && value.writing_version !== original.expected_draft_version + (original.writing_mode === 'consume' ? 1 : 0))
      || value.writing_disposition !== (original.writing_id === null ? 'none' : original.writing_mode === 'consume' ? 'consumed' : 'preserved')))) {
    throw new Error('invalid_message_acknowledgement');
  }
  return clone(value);
}
const rejectionCodes = new Set(['feedback_version_conflict', 'conversation_closed', 'conversation_lifecycle_conflict',
  'writing_version_conflict', 'writing_base_unavailable', 'writing_base_conflict', 'writing_content_conflict',
  'writing_version_exhausted', 'conversation_sequence_exhausted']);
function acknowledgement(value: FeedbackPendingResult, attempt: FeedbackPendingAttemptRequest | null,
  original?: AppendFeedbackMessageRequest): FeedbackPendingResult {
  if (!value || !keys(value, 'schema_version,pending_send_id,attempt_version,phase,conversation_id,text,quick_label,writing_mode,rejection_code,message')
    || value.schema_version !== 'conversation-pending-result.v1' || !uuid(value.pending_send_id) || !uuid(value.conversation_id)
    || !Number.isInteger(value.attempt_version) || value.attempt_version < 1 || value.attempt_version > 2147483647
    || !['reserved', 'accepted', 'rejected', 'accepted_resolved', 'rejected_resolved'].includes(value.phase)
    || !['consume', 'preserve'].includes(value.writing_mode)
    || (attempt && (!same(attempt.pending_send_id, value.pending_send_id) || attempt.expected_attempt_version !== value.attempt_version))
    || (value.phase.endsWith('_resolved') ? value.text !== null || value.quick_label !== null
      : typeof value.text !== 'string' || [...value.text].length > 1000
        || (value.quick_label !== null && !['got_it', 'need_help', 'tried_this'].includes(value.quick_label)))
    || (value.phase.startsWith('accepted') ? !value.message || value.rejection_code !== null
      : value.message !== null || (value.phase === 'reserved' ? value.rejection_code !== null
        : value.rejection_code === null || !rejectionCodes.has(value.rejection_code)))
    || (original && (value.writing_mode !== original.writing_mode
      || (!value.phase.endsWith('_resolved') && (value.text !== pythonTrim(original.text ?? '')
        || value.quick_label !== (original.quick_label ?? null)))))) throw new Error('invalid_pending_acknowledgement');
  if (value.message) {
    const message = receipt(value.message, original);
    if (!same(message.conversation_id, value.conversation_id)) throw new Error('invalid_pending_acknowledgement');
  }
  return clone(value);
}

function recovery(scope: FeedbackConversationScope, target: FeedbackConversationTarget,
  reservation: FeedbackSendReservationRequest | null, initialAttempt: FeedbackPendingAttemptRequest | null) {
  const capturedScope = { ...scope }; const capturedTarget = { ...target };
  const original = reservation ? clone(reservation) : null;
  const boundary = captureFeedbackConversationBoundary(capturedScope);
  let provedAttempt: { disposition: 'accepted' | 'rejected'; rejection: string | null } | null = null;
  let provedReceipt: string | null = null;
  let terminalResult: FeedbackPendingResult | null = null;
  let reservationDispatched = false;
  let conversationId: string | null = null;
  let attempt = initialAttempt ? clone(initialAttempt) : null;
  let state: FeedbackSendState = { status: 'ready' };
  let reconciling: Promise<FeedbackMessageResult> | null = null;
  let inFlight: { action: string; promise: Promise<FeedbackPendingResult> } | null = null;
  const options = (signal?: AbortSignal) => ({ scope: capturedScope, signal });
  const assertCurrent = (signal?: AbortSignal) => {
    if (!boundary.current() || signal?.aborted) throw new DOMException('Conversation context changed', 'AbortError');
  };
  function rememberReceipt(value: FeedbackMessageReceipt): void {
    const identity = JSON.stringify([value.message_id.toLowerCase(), value.conversation_id.toLowerCase(),
      value.feedback_revision_id.toLowerCase(), value.sequence, value.writing_id?.toLowerCase() ?? null,
      value.writing_version, value.writing_disposition]);
    if (provedReceipt !== null && provedReceipt !== identity) throw new Error('contradictory_message_acknowledgement');
    provedReceipt = identity;
  }
  function accept(value: FeedbackPendingResult): FeedbackPendingResult {
    const result = acknowledgement(value, attempt, original?.command);
    if (conversationId !== null && !same(conversationId, result.conversation_id)) throw new Error('invalid_pending_acknowledgement');
    if (terminalResult && result.phase !== terminalResult.phase) throw new Error('contradictory_pending_acknowledgement');
    const disposition = result.phase.startsWith('accepted') ? 'accepted' : result.phase.startsWith('rejected') ? 'rejected' : null;
    if (provedAttempt && (provedAttempt.disposition !== disposition || provedAttempt.rejection !== result.rejection_code)) {
      throw new Error('contradictory_pending_acknowledgement');
    }
    if (result.message) rememberReceipt(result.message);
    if (disposition) provedAttempt = { disposition, rejection: result.rejection_code };
    if (result.phase.endsWith('_resolved')) terminalResult = clone(result);
    conversationId = result.conversation_id;
    attempt = { schema_version: 'conversation-pending-attempt.v1', pending_send_id: result.pending_send_id,
      expected_attempt_version: result.attempt_version };
    state = { status: result.phase, result };
    // Receipts never overwrite cached/private writing or selected revision.
    void Promise.resolve().then(() => {
      if (boundary.current()) return refreshFeedbackConversation(capturedScope, capturedTarget);
    }).catch(() => {});
    return clone(result);
  }
  function run(action: 'send' | 'resolve', signal?: AbortSignal): Promise<FeedbackPendingResult> {
    try { assertCurrent(signal); } catch (error) { return Promise.reject(error); }
    if (reconciling) return Promise.reject(new Error('send_action_pending'));
    if (inFlight) return inFlight.action === action ? inFlight.promise : Promise.reject(new Error('send_action_pending'));
    if (action === 'resolve' && (!attempt || !['accepted', 'rejected', 'accepted_resolved', 'rejected_resolved'].includes(state.status))) {
      return Promise.reject(new Error('send_resolution_requires_proved_result'));
    }
    const promise = (async () => {
      state = { status: 'sending' };
      try {
        if (action === 'resolve') {
          const value = await resolveFeedbackSend(capturedTarget, clone(attempt!), options(signal));
          assertCurrent(signal);
          if (!value.phase.endsWith('_resolved')) throw new Error('invalid_send_resolution');
          return accept(value);
        }
        if (original && !attempt) {
          // A lost rearm ACK is looked up without rearming again. The immutable
          // command remains identical; only the explicit retry control is omitted.
          const body = clone(original);
          if (original.retry && original.retry.expected_attempt_version >= 2147483647) throw new Error('pending_attempt_exhausted');
          if (reservationDispatched && original.retry) body.retry = null;
          reservationDispatched = true;
          // This response follows reservation COMMIT. Execution is a new request.
          const reserved = await reserveFeedbackSend(capturedTarget, body, options(signal));
          assertCurrent(signal);
          if (original.retry && (!same(reserved.pending_send_id, original.retry.pending_send_id)
            || reserved.attempt_version !== original.retry.expected_attempt_version + 1)) throw new Error('rearmed_attempt_unresolved');
          accept(reserved);
          if (reserved.phase !== 'reserved') return clone(reserved);
        }
        if (!attempt) throw new Error('send_attempt_required');
        const value = await executeFeedbackSend(capturedTarget, clone(attempt), options(signal));
        assertCurrent(signal); return accept(value);
      } catch (error) {
        // HTTP errors, missing receipts, timeouts and aborts never prove rejection
        // or release the durable slot. Retry this same command/attempt explicitly.
        state = { status: 'unknown' }; throw error;
      } finally { inFlight = null; }
    })();
    inFlight = { action, promise }; return promise;
  }
  return {
    getState(): FeedbackSendState { return boundary.current() ? clone(state) : { status: 'unavailable' }; },
    getIntent(): FeedbackSendReservationRequest | null { return boundary.current() && original ? clone(original) : null; },
    getAttempt(): FeedbackPendingAttemptRequest | null { return boundary.current() && attempt ? clone(attempt) : null; },
    send: (signal?: AbortSignal) => run('send', signal),
    resolve: (signal?: AbortSignal) => run('resolve', signal),
    reconcile(signal?: AbortSignal): Promise<FeedbackMessageResult> {
      try {
        assertCurrent(signal);
        if (!original) throw new Error('original_send_required');
        if (inFlight) throw new Error('send_action_pending');
      } catch (error) { return Promise.reject(error); }
      if (reconciling) return reconciling;
      reconciling = (async () => {
        try {
          const value = await reconcileFeedbackMessage(capturedTarget, clone(original!.command), options(signal));
          assertCurrent(signal);
          if (value.schema_version !== 'conversation-message-result.v1') throw new Error('invalid_message_acknowledgement');
          const { schema_version, ...message } = value;
          const result = { schema_version, ...receipt(message, original!.command) };
          if (conversationId !== null && !same(conversationId, result.conversation_id)) throw new Error('invalid_message_acknowledgement');
          // Receipt identity belongs to the original operation, not a guessed
          // attempt. A later rearm may be accepted after an old proved rejection.
          rememberReceipt(result);
          conversationId = result.conversation_id;
          state = terminalResult?.phase === 'accepted_resolved'
            ? { status: 'accepted_resolved', result: clone(terminalResult) } : { status: 'reconciled', result };
          void Promise.resolve().then(() => {
            if (boundary.current()) return refreshFeedbackConversation(capturedScope, capturedTarget);
          }).catch(() => {});
          return clone(result);
        } catch (error) { state = { status: 'unknown' }; throw error; }
        finally { reconciling = null; }
      })();
      return reconciling;
    },
    dispose: () => boundary.dispose(),
  };
}

export function captureFeedbackSend(scope: FeedbackConversationScope, target: FeedbackConversationTarget,
  reservation: FeedbackSendReservationRequest) {
  return recovery(scope, target, reservation, null);
}

/** Reload recovery executes the server's stored immutable command, never rebuilds it from prose. */
export function captureFeedbackPendingAttempt(scope: FeedbackConversationScope, target: FeedbackConversationTarget,
  attempt: FeedbackPendingAttemptRequest) {
  return recovery(scope, target, null, attempt);
}
